import { SourceType } from '@sc/graphql';
import { ReactElement, Suspense, useMemo } from 'react';
import {
  sourceDisplayPath,
  SourceIcon,
  sourceIconMap,
  sourceNiceName,
} from '../util/sources';
import { Drawer } from './Drawer';
import { useSuspenseQuery } from '@apollo/client/react';
import { GQL_QUERY_INDEX_GROUNDING_CHUNKS } from '../../queries';
import { FullLoader } from './Loader';
import { useTheme } from '../providers/ThemeContext';
import classNames from 'classnames';

interface Props {
  sources: Array<{
    uuid: string;
    source: { type: SourceType };
  }>;
  queryUuid: string;
  message: {
    uuid: string;
    createdAt: string;
  };
  open: boolean;
  setOpen: (open: boolean) => void;
  /** The chips' numbering — rows show and are ordered by it. */
  numberByDocUuid?: Map<string, number> | undefined;
}

const SourceCountInner = ({
  message,
  queryUuid,
  numberByDocUuid,
}: Pick<Props, 'message' | 'numberByDocUuid' | 'queryUuid'>) => {
  const { inverted } = useTheme();

  const { data } = useSuspenseQuery(GQL_QUERY_INDEX_GROUNDING_CHUNKS, {
    variables: {
      input: {
        queryUuid,
        message,
      },
    },
    context: {
      isBatched: true
    }
  });

  const rows = [...data.indexGroundingChunks].sort(
    (a, b) =>
      (numberByDocUuid?.get(a.uuid) ?? Number.MAX_SAFE_INTEGER) -
      (numberByDocUuid?.get(b.uuid) ?? Number.MAX_SAFE_INTEGER)
  );

  return (
    <div
      className={classNames(
        'py-5 space-y-5 overflow-y-auto overflow-x-hidden overscroll-contain w-full sm:min-w-90 sm:max-w-110',
        inverted ? 'bg-white text-black' : 'bg-background-tertiary'
      )}
    >
      {rows.map((sc, idx) => {
        const number = numberByDocUuid?.get(sc.uuid);

        return (
        <div
          className={classNames(
            'border-b pb-5 flex flex-col px-5 min-w-0',
            inverted ? 'border-black-t-800' : 'border-grey-t-800'
          )}
          key={idx}
        >
          <div className="flex items-center space-x-2">
            {number !== undefined && (
              <span
                className={classNames(
                  'flex items-center justify-center rounded-full size-5 shrink-0 border text-xs leading-none font-medium',
                  inverted
                    ? 'border-black-t-700 bg-black-t-900 text-black-t-200'
                    : 'text-text-low-priority border-grey-t-700 bg-background-secondary'
                )}
              >
                {number}
              </span>
            )}
            <SourceIcon className="size-5 shrink-0" type={sc.source.type} />
            <p className="font-semibold">{sourceNiceName[sc.source.type]}</p>
          </div>
          <div
            className={classNames(
              'text-body-smallest mt-2 min-w-0 space-y-0.5',
              inverted ? 'text-grey-600' : 'text-text-low-priority'
            )}
          >
            {sourceDisplayPath[sc.source.type](sc)}
          </div>
        </div>
        );
      })}
    </div>
  );
};

export const SourceCount = ({
  sources,
  queryUuid,
  message,
  open,
  setOpen,
  numberByDocUuid,
}: Props): ReactElement => {
  const { inverted } = useTheme();

  const sourceTypes = useMemo(
    () =>
      (Object.keys(sourceIconMap) as SourceType[]).filter((type) =>
        sources.some((s) => s.source.type === type)
      ),
    [sources]
  );

  const uniqueSources = new Set(sources.map((s) => s.uuid)).size;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-open={open}
        className={classNames(
          'p-2 space-x-1.5 active:shadow-focused-dark rounded-[20px] w-fit flex items-center',
          inverted
            ? 'border border-black-t-800 shadow-xs hover:bg-black-t-900'
            : 'bg-background-tertiary hover:bg-grey-t-800'
        )}
      >
        <div className="flex">
          {sourceTypes.map((sc, index, arr) => (
            <div
              className={classNames(
                'bg-white rounded-full p-1 outline-[2.5px]',
                inverted ? 'outline-white' : 'outline-grey-900'
              )}
              style={{
                marginLeft: `-${index * 2.5}px`,
                zIndex: (arr.length - index) * 10,
              }}
            >
              <SourceIcon className="size-4 text-black" type={sc} />
            </div>
          ))}
        </div>
        <span
          className={classNames(
            'text-sm leading-none',
            inverted ? 'text-grey-600' : 'text-text-subtle'
          )}
        >
          {uniqueSources} sources
        </span>
      </button>
      <Drawer
        setOpen={setOpen}
        title={`Sources (${uniqueSources})`}
        open={open}
      >
        <Suspense fallback={<FullLoader />}>
          <SourceCountInner
            queryUuid={queryUuid}
            message={message}
            numberByDocUuid={numberByDocUuid}
          />
        </Suspense>
      </Drawer>
    </>
  );
};
